/**
 * Zustand store to manage the scheduling flow
 * Utilizes Zustand for state management and localforage for persistence
 * It is agnostic to the MSO, if a patient is scheduling an initial appointment or a refill sync review, it will store the date, time, and timezone
 */

import { DateValue } from "@mantine/dates";
import { create, StateCreator } from "zustand";
import dayjs from "dayjs";
import { persist } from "zustand/middleware";
import localforage from "localforage";

interface scheduleFlowType {
  date: DateValue | string | null;
  setDate: (date: DateValue | string) => void;
  time: string | null;
  setTime: (time: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  clearSchedulingStore: () => void;
}

const storage = {
  getItem: async (name: string) => {
    const value = await localforage.getItem(name);
    return value ? JSON.parse(value as string) : null;
  },
  setItem: async (name: string, value: unknown) => {
    await localforage.setItem(name, JSON.stringify(value));
  },
  removeItem: async (name: string) => {
    await localforage.removeItem(name);
  },
};

export const useSchedulingStore = create<scheduleFlowType>(
  persist(
    (set) => ({
      date: null,
      setDate: (date: DateValue | string) => set({ date }),
      time: null,
      setTime: (time: string) => set({ time }),
      timezone: dayjs.tz.guess(),
      setTimezone: (timezone: string) => set({ timezone }),
      clearSchedulingStore: () => {
        set({
          date: null,
          time: null,
          timezone: dayjs.tz.guess(),
        });
        localforage.removeItem("schedule-flow-state");
      },
    }),
    {
      name: "schedule-flow-state",
      storage,
    }
  ) as StateCreator<scheduleFlowType>
);

export const clearSchedulingData = async () => {
  await localforage.removeItem("schedule-flow-state");
};
